//#region Imports
import { IAnydoElectronPreload, IAnydoAuth, IContactRecord } from "@anydo/interfaces";
import {
  SETTINGS_API_ORIGIN_URL,
  escapedUrl,
  SETTINGS_GOOGLE_CLIENT_ID,
  SETTINGS_GOOGLE_LOOPBACK_PORT,
  HttpError,
  isHttpError,
} from "@anydo/core";
import { AuthGoogleScope, AUTH_DEFAULT_GOOGLE_SCOPES, AUTH_GOOGLE_OAUTH_QUERY_PARAM } from "../auth-constants";
import { createAuthHeaders } from "../utils/createAuthHeaders";
import { fetchGoogleContacts } from "./fetchGoogleContacts";
import { loadGSI } from "./loadGSI";
import { loadGapi } from "./loadGapi";

declare var AnydoElectronPreload: IAnydoElectronPreload | void;
//#endregion

//#region Types
type AnydoGoogleTokenContextT = "web_client_calendar" | "web_client_contacts";
//#endregion

//#region Config
const POST_TOKEN_URL = escapedUrl`${SETTINGS_API_ORIGIN_URL}/me/exchange-google-tokens`;
const GET_TOKEN_URL = escapedUrl`${SETTINGS_API_ORIGIN_URL}/me/get-google-access-token`;
//#endregion

//#region Public
export async function getGoogleTokenByGoogleLogin({
  clientId = SETTINGS_GOOGLE_CLIENT_ID,
  scope = AUTH_DEFAULT_GOOGLE_SCOPES,
}: {
  clientId?: string;
  scope?: readonly string[];
} = {}): Promise<string> {
  if (typeof AnydoElectronPreload !== "undefined") {
    throw new Error("getGoogleTokenByGoogleLogin(): Shouldn't be called in Electron applications");
  }

  return new Promise((resolve, reject) => {
    return loadGSI().then((google) => {
      const tokenClient = google.accounts.oauth2.initTokenClient({
        client_id: clientId,
        scope: scope.join(" "),
        prompt: "select_account",
        error_callback: (x) => reject(x),
        callback: (response) => {
          if (response && response.access_token) {
            resolve(response.access_token);
          }

          reject(response.error);
        },
      });
      tokenClient.requestAccessToken();
    });
  });
}
//#endregion

export function isGSuiteAccount(email: string) {
  return email.split("@")[1] !== "gmail.com";
}

//#region Public - contacts
export async function getGoogleContacts(auth: IAnydoAuth): Promise<ReadonlyArray<IContactRecord>> {
  if (typeof AnydoElectronPreload !== "undefined") {
    return AnydoElectronPreload.thirdPartyAuth.getGoogleContacts();
  }

  const authToken = auth.getAuthToken();
  if (authToken == null) throw new Error("getGoogleContacts(): Cannot be called when user is logged out");

  try {
    const token = await getGoogleAccessToken("web_client_contacts", authToken);
    return await fetchGoogleContacts(token);
  } catch (error) {
    if (!isHttpError(error) || error.status !== 0) {
      throw new Error("getGoogleContacts(): failed", { cause: error });
    }
  }

  const { code } = await grantOfflineAccess({ scope: AuthGoogleScope.CONTACTS_READONLY });
  const token = await exchangeGoogleTokens("web_client_contacts", authToken, code);
  return fetchGoogleContacts(token);
}
//#endregion

export async function grantOfflineAccess(options: { scope: string }): Promise<{ code: string }> {
  return new Promise((resolve, reject) => {
    loadGSI().then(() => {
      const codeClient = google.accounts.oauth2.initCodeClient({
        client_id: SETTINGS_GOOGLE_CLIENT_ID,
        scope: options.scope,
        select_account: true,
        error_callback: (x) => reject(x),
        callback: (response) => {
          if (response && response.code) {
            resolve({ code: response.code });
          }

          reject(response.error);
        },
      });
      codeClient.requestCode();
    });
  });
}

export class InsufficientScopeError extends Error {
  constructor(public readonly scopes: string[]) {
    super(`Insufficient scopes: ${scopes.length}`);
  }
}

export async function getGoogleAuthCode(): Promise<{ code: string; redirectUri?: string }> {
  if (typeof AnydoElectronPreload !== "undefined") {
    return AnydoElectronPreload.thirdPartyAuth.getGoogleCalendarAuthCode().then((data) => ({
      code: data,
      redirectUri: escapedUrl`http://127.0.0.1:${SETTINGS_GOOGLE_LOOPBACK_PORT}/?${AUTH_GOOGLE_OAUTH_QUERY_PARAM}=1`,
    }));
  }
  return new Promise((resolve, reject) => {
    const scopes = [
      ...AUTH_DEFAULT_GOOGLE_SCOPES,
      AuthGoogleScope.CALENDAR,
      AuthGoogleScope.CONTACTS_READONLY,
      AuthGoogleScope.CONTACTS_OTHER_READONLY,
      AuthGoogleScope.DIRECTORY_READONLY,
    ];

    loadGSI().then(() => {
      const codeClient = google.accounts.oauth2.initCodeClient({
        client_id: SETTINGS_GOOGLE_CLIENT_ID,
        scope: scopes.join(" "),
        select_account: true,
        error_callback: (x) => reject(x),
        callback: (response) => {
          if (response && response.code) {
            const grantedScopes = new Set(response.scope.split(" "));
            const missingScopes = scopes.filter((it) => !grantedScopes.has(it));

            if (missingScopes.length) {
              reject(new InsufficientScopeError(missingScopes));
            }

            resolve({
              code: response.code,
              redirectUri: undefined,
            });
          }

          reject(response.error);
        },
      });
      codeClient.requestCode();
    });
  });
}

export async function openGoogleDrivePicker(options?: {
  signal?: AbortSignal;
  onPickerMounted?: () => void;
  locale?: string;
}) {
  await loadGapi("picker");
  options?.signal?.throwIfAborted();

  const accessToken =
    typeof AnydoElectronPreload !== "undefined" ?
      await AnydoElectronPreload.thirdPartyAuth.getGoogleAccessToken({
        scopes: [AuthGoogleScope.DRIVE_METADATA],
      })
    : await getGoogleTokenByGoogleLogin({
        scope: [AuthGoogleScope.DRIVE_METADATA],
      });

  options?.signal?.throwIfAborted();

  const { resolve, reject, promise } = Promise.withResolvers<google.picker.ResponseObject>();
  options?.signal?.addEventListener("abort", reject, { once: true });

  const view = new google.picker.DocsView(google.picker.ViewId.DOCS).setIncludeFolders(true);
  const builder = new google.picker.PickerBuilder()
    .setOAuthToken(accessToken)
    .enableFeature(google.picker.Feature.NAV_HIDDEN)
    .addView(view)
    .setCallback((data) => {
      if (data.action === "picked" || data.action === "cancel") {
        resolve(data);
      }
    });

  if (options?.locale !== undefined) {
    builder.setLocale(options?.locale as Parameters<(typeof builder)["setLocale"]>[0]);
  }

  const picker = builder.build();
  picker.setVisible(true);
  options?.onPickerMounted?.();

  return promise;
}

//#endregion

//#region Private utils
async function getGoogleAccessToken(context: AnydoGoogleTokenContextT, authToken: string): Promise<string> {
  interface IResponseData {
    access_token: string;
  }

  const url = new URL(GET_TOKEN_URL);
  url.searchParams.set("context", context);

  const response = await fetch(url.toString(), {
    headers: createAuthHeaders(authToken),
  });

  if (!response.ok) {
    throw new HttpError(response, 0, undefined);
  }

  const { access_token } = (await response.json()) as IResponseData;
  return access_token;
}

async function exchangeGoogleTokens(
  context: AnydoGoogleTokenContextT,
  authToken: string,
  googleAccessToken: string,
): Promise<string> {
  interface IResponseData {
    access_token: string;
  }

  const response = await fetch(POST_TOKEN_URL, {
    method: "POST",
    headers: createAuthHeaders(authToken),
    body: JSON.stringify({
      context,
      code: googleAccessToken,
    }),
  });

  if (response.ok) {
    const { access_token } = (await response.json()) as IResponseData;
    return access_token;
  } else {
    throw new Error(`exchangeGoogleTokens(): Error (${response.status}): ${response.statusText}`);
  }
}
//#endregion
